import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { messagingAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import {
    MessageSquare, Send, Search, Plus, ArrowLeft, Users, ChevronRight,
    Loader2, Building2, User, Phone, Euro, Home, Clock, FileText,
    AlertTriangle, Info, Sparkles, CheckCircle2, Trash2, Bell, X
} from 'lucide-react';

// ─── Constants ─────────────────────────────────────────
const roleBadge = (role) => {
    const map = {
        admin: { label: 'Admin', cls: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
        agent: { label: 'Agent', cls: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
        seller: { label: 'Vendeur', cls: 'bg-green-500/20 text-green-400 border-green-500/30' },
    };
    return map[role] || { label: role, cls: 'bg-white/10 text-white/60 border-white/20' };
};

const initials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
};

const GUIDED_ICONS = {
    callback: Phone, price: Euro, visit: Home,
    timeline: Clock, docs: FileText, issue: AlertTriangle, info: Info,
};

// ─── Guided Templates Panel (Seller) ──────────────────
function GuidedPanel({ templates, onSelect, isDark }) {
    return (
        <div className="p-3 space-y-2" data-testid="guided-templates-panel">
            <p className={cn("text-xs font-semibold px-1 mb-1", isDark ? "text-white/50" : "text-slate-500")}>
                Demandes rapides
            </p>
            <div className="grid grid-cols-2 gap-2">
                {templates.map(t => {
                    const Icon = GUIDED_ICONS[t.id] || Info;
                    return (
                        <button
                            key={t.id}
                            onClick={() => onSelect(t)}
                            className={cn(
                                "flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all text-sm",
                                isDark
                                    ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                                    : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                            )}
                            data-testid={`guided-${t.id}`}
                        >
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${t.color}20` }}>
                                <Icon className="w-4 h-4" style={{ color: t.color }} />
                            </div>
                            <span className={cn("font-medium leading-tight", isDark ? "text-white" : "text-slate-900")}>{t.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── AI Reminders Panel ────────────────────────────────
function AiRemindersPanel({ conversationId, isDark, onClose }) {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [extracting, setExtracting] = useState(false);

    const loadReminders = useCallback(async () => {
        try {
            const res = await messagingAPI.getAiReminders(false);
            const convReminders = (res.data.reminders || []).filter(r => r.conversation_id === conversationId);
            setReminders(convReminders);
        } catch { /* ignore */ }
    }, [conversationId]);

    useEffect(() => { loadReminders(); }, [loadReminders]);

    const handleExtract = async () => {
        setExtracting(true);
        try {
            await messagingAPI.extractReminders(conversationId);
            await loadReminders();
        } catch { /* ignore */ }
        setExtracting(false);
    };

    const handleComplete = async (id) => {
        await messagingAPI.completeAiReminder(id);
        setReminders(prev => prev.filter(r => r.id !== id));
    };

    const handleDelete = async (id) => {
        await messagingAPI.deleteAiReminder(id);
        setReminders(prev => prev.filter(r => r.id !== id));
    };

    const priorityColor = (p) => {
        if (p === 'haute') return 'text-red-400 bg-red-500/10 border-red-500/20';
        if (p === 'moyenne') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    };

    return (
        <div className={cn("border-l flex flex-col h-full w-72 shrink-0", isDark ? "border-white/10 bg-card/80" : "border-slate-200 bg-slate-50")} data-testid="ai-reminders-panel">
            <div className={cn("p-3 border-b flex items-center justify-between", isDark ? "border-white/10" : "border-slate-200")}>
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#FF914D]" />
                    <h3 className="font-bold text-sm">Rappels IA</h3>
                </div>
                <button onClick={onClose} className="p-1 rounded hover:bg-white/10">
                    <X className="w-4 h-4 text-muted-foreground" />
                </button>
            </div>
            <div className="p-3">
                <Button
                    onClick={handleExtract}
                    disabled={extracting}
                    size="sm"
                    className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white text-xs h-8"
                    data-testid="extract-reminders-btn"
                >
                    {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                    Analyser la conversation
                </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {reminders.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-6">
                        Cliquez sur "Analyser" pour extraire les rappels de cette conversation
                    </p>
                ) : reminders.map(r => (
                    <div key={r.id} className={cn("p-3 rounded-xl border text-xs", isDark ? "bg-white/5 border-white/10" : "bg-white border-slate-200")} data-testid={`reminder-${r.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                            <p className={cn("font-semibold text-sm leading-tight", isDark ? "text-white" : "text-slate-900")}>{r.title}</p>
                            <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border font-bold shrink-0", priorityColor(r.priority))}>
                                {r.priority}
                            </span>
                        </div>
                        <p className="text-muted-foreground mb-2 leading-relaxed">{r.description}</p>
                        {r.suggested_date && (
                            <p className="text-[#FF914D] font-medium mb-2 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {r.suggested_date}
                            </p>
                        )}
                        <div className="flex gap-1.5">
                            <button onClick={() => handleComplete(r.id)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors" data-testid={`complete-reminder-${r.id}`}>
                                <CheckCircle2 className="w-3 h-3" /> Fait
                            </button>
                            <button onClick={() => handleDelete(r.id)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" data-testid={`delete-reminder-${r.id}`}>
                                <Trash2 className="w-3 h-3" /> Suppr.
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── New conversation modal ───────────────────────────
function NewConversationPanel({ onClose, onCreated, isDark, contactType }) {
    const [contacts, setContacts] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedContact, setSelectedContact] = useState(null);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        setLoading(true);
        messagingAPI.getContacts(search || null, contactType).then(r => {
            setContacts(r.data.contacts || []);
        }).finally(() => setLoading(false));
    }, [search, contactType]);

    const handleCreate = async () => {
        if (!selectedContact || !message.trim()) return;
        setSending(true);
        try {
            const res = await messagingAPI.createConversation(selectedContact.id, null, message.trim());
            onCreated(res.data.conversation_id);
        } catch { /* error */ }
        setSending(false);
    };

    if (selectedContact) {
        return (
            <div className="flex flex-col h-full">
                <div className={cn("p-4 border-b flex items-center gap-3", isDark ? "border-white/10" : "border-slate-200")}>
                    <button onClick={() => setSelectedContact(null)} className={cn("p-1.5 rounded-lg", isDark ? "hover:bg-white/10" : "hover:bg-slate-100")}>
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white text-xs font-bold">
                        {initials(selectedContact.name)}
                    </div>
                    <div>
                        <p className="font-semibold text-sm">{selectedContact.name}</p>
                        <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border", roleBadge(selectedContact.role).cls)}>
                            {roleBadge(selectedContact.role).label}
                        </span>
                    </div>
                </div>
                <div className="flex-1 p-4 flex flex-col justify-end">
                    <textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder="Écrivez votre premier message..."
                        className={cn(
                            "w-full h-32 rounded-xl p-3 resize-none text-sm border focus:outline-none focus:ring-2 focus:ring-[#681793]/50",
                            isDark ? "bg-white/5 border-white/10 text-white placeholder:text-white/30" : "bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400"
                        )}
                        data-testid="new-message-input"
                    />
                    <Button onClick={handleCreate} disabled={!message.trim() || sending} className="mt-3 w-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold h-11" data-testid="send-first-message-btn">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Envoyer
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            <div className={cn("p-4 border-b", isDark ? "border-white/10" : "border-slate-200")}>
                <div className="flex items-center gap-3 mb-3">
                    <button onClick={onClose} className={cn("p-1.5 rounded-lg", isDark ? "hover:bg-white/10" : "hover:bg-slate-100")}>
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <h3 className="font-bold">Nouvelle conversation</h3>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Rechercher un contact..."
                        className={cn(
                            "w-full pl-9 pr-4 h-9 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#681793]/50",
                            isDark ? "bg-white/5 border-white/10 text-white placeholder:text-white/30" : "bg-slate-50 border-slate-200"
                        )}
                        data-testid="search-contacts-input"
                    />
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
                {loading ? (
                    <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : contacts.length === 0 ? (
                    <p className="text-center text-muted-foreground text-sm p-6">Aucun contact trouvé</p>
                ) : contacts.map(c => (
                    <button key={c.id} onClick={() => setSelectedContact(c)} className={cn("w-full flex items-center gap-3 p-3 rounded-xl transition-colors", isDark ? "hover:bg-white/5" : "hover:bg-slate-50")} data-testid={`contact-${c.id}`}>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {initials(c.name)}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                            <p className="font-medium text-sm truncate">{c.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                        </div>
                        <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border shrink-0", roleBadge(c.role).cls)}>
                            {roleBadge(c.role).label}
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─── Conversation list ────────────────────────────────
function ConversationList({ conversations, activeId, onSelect, onNewClick, isDark, userId }) {
    return (
        <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 flex items-center justify-center mb-4">
                        <MessageSquare className="w-8 h-8 text-[#FF914D]" />
                    </div>
                    <p className="font-semibold mb-1">Aucune conversation</p>
                    <p className="text-sm text-muted-foreground mb-4">Démarrez un échange</p>
                    <Button onClick={onNewClick} size="sm" className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white" data-testid="empty-new-conv-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau message
                    </Button>
                </div>
            ) : conversations.map(conv => {
                const isGroup = conv.is_group || conv.conversation_type === 'agency_group';
                const other = isGroup
                    ? null
                    : (conv.participant_details || []).find(p => p.id !== userId) || {};
                const displayName = isGroup ? (conv.subject || 'Groupe Agence') : (other?.name || 'Conversation');
                const isActive = conv.id === activeId;
                return (
                    <button
                        key={conv.id}
                        onClick={() => onSelect(conv.id)}
                        className={cn(
                            "w-full flex items-center gap-3 p-3.5 border-b transition-colors text-left",
                            isDark ? "border-white/5" : "border-slate-100",
                            isActive
                                ? isDark ? "bg-white/10" : "bg-slate-100"
                                : isDark ? "hover:bg-white/5" : "hover:bg-slate-50"
                        )}
                        data-testid={`conversation-${conv.id}`}
                    >
                        <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0",
                            isGroup ? "bg-gradient-to-br from-[#681793] to-[#3b82f6]" : "bg-gradient-to-br from-[#681793] to-[#FF914D]"
                        )}>
                            {isGroup ? <Users className="w-5 h-5" /> : initials(other?.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                                <p className={cn("font-semibold text-sm truncate", conv.unread_count > 0 && "text-[#FF914D]")}>
                                    {displayName}
                                </p>
                                {conv.last_message && (
                                    <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                                        {new Date(conv.last_message.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                                    </span>
                                )}
                            </div>
                            {!isGroup && (other?.agency_name || conv.subject) && (
                                <p className="text-[10px] text-[#A855F7] truncate -mt-0.5 mb-0.5" data-testid="conversation-context">
                                    {[other?.agency_name, conv.subject ? conv.subject.replace(/^Vente\s*[—–-]\s*/i, '') : null].filter(Boolean).join(' · ')}
                                </p>
                            )}
                            <div className="flex items-center gap-1.5">
                                {isGroup && <Users className="w-3 h-3 text-muted-foreground shrink-0" />}
                                {!isGroup && other?.role && (
                                    <span className={cn("text-[9px] px-1 py-0.5 rounded border shrink-0", roleBadge(other.role).cls)}>
                                        {roleBadge(other.role).label}
                                    </span>
                                )}
                                <p className="text-xs text-muted-foreground truncate">
                                    {conv.last_message ? conv.last_message.content : 'Pas de message'}
                                </p>
                            </div>
                        </div>
                        {conv.unread_count > 0 && (
                            <span className="w-5 h-5 rounded-full bg-[#FF914D] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                {conv.unread_count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

// ─── Chat view ────────────────────────────────────────
function ChatView({ conversationId, onBack, isDark, userId, userRole }) {
    const [messages, setMessages] = useState([]);
    const [convInfo, setConvInfo] = useState(null);
    const [newMsg, setNewMsg] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showReminders, setShowReminders] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [showGuided, setShowGuided] = useState(false);
    const [guidedCategory, setGuidedCategory] = useState(null);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    const isSeller = userRole === 'seller';
    const isAgentOrAdmin = userRole === 'admin' || userRole === 'agent';

    const loadMessages = useCallback(async () => {
        try {
            const res = await messagingAPI.getMessages(conversationId);
            setMessages(res.data.messages || []);
            setConvInfo(res.data.conversation || null);
        } catch { /* */ }
        setLoading(false);
    }, [conversationId]);

    useEffect(() => {
        setLoading(true);
        setShowReminders(false);
        loadMessages();
        const interval = setInterval(loadMessages, 5000);
        return () => clearInterval(interval);
    }, [loadMessages]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (isSeller) {
            messagingAPI.getGuidedTemplates().then(r => setTemplates(r.data.templates || [])).catch(() => {});
        }
    }, [isSeller]);

    const handleSend = async () => {
        if (!newMsg.trim() || sending) return;
        setSending(true);
        try {
            await messagingAPI.sendMessage(conversationId, newMsg.trim(), guidedCategory ? 'guided' : 'text', guidedCategory);
            setNewMsg('');
            setGuidedCategory(null);
            setShowGuided(false);
            await loadMessages();
            inputRef.current?.focus();
        } catch { /* */ }
        setSending(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const handleGuidedSelect = (template) => {
        setNewMsg(template.prompt);
        setGuidedCategory(template.id);
        setShowGuided(false);
        inputRef.current?.focus();
    };

    const isGroup = convInfo?.conversation_type === 'agency_group';
    const other = !isGroup ? (convInfo?.participant_details || []).find(p => p.id !== userId) || {} : null;
    const displayName = isGroup ? (convInfo?.subject || 'Groupe Agence') : (other?.name || 'Conversation');
    const participantCount = (convInfo?.participants || []).length;

    return (
        <div className="flex flex-col h-full flex-1">
            {/* Header */}
            <div className={cn("p-3 border-b flex items-center gap-3", isDark ? "border-white/10" : "border-slate-200")}>
                <button onClick={onBack} className={cn("md:hidden p-1.5 rounded-lg", isDark ? "hover:bg-white/10" : "hover:bg-slate-100")} data-testid="chat-back-btn">
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold",
                    isGroup ? "bg-gradient-to-br from-[#681793] to-[#3b82f6]" : "bg-gradient-to-br from-[#681793] to-[#FF914D]"
                )}>
                    {isGroup ? <Users className="w-4 h-4" /> : initials(other?.name)}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{displayName}</p>
                    {isGroup ? (
                        <span className="text-[10px] text-muted-foreground">{participantCount} membres</span>
                    ) : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {other?.role && (
                                <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border", roleBadge(other.role).cls)}>
                                    {roleBadge(other.role).label}
                                </span>
                            )}
                            {other?.agency_name && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-[#681793]/30 bg-[#681793]/10 text-[#A855F7]" data-testid="conversation-header-agency">
                                    {other.agency_name}
                                </span>
                            )}
                            {convInfo?.subject && (
                                <span className="text-[10px] text-muted-foreground truncate" data-testid="conversation-header-property">
                                    · {convInfo.subject.replace(/^Vente\s*[—–-]\s*/i, '')}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {isAgentOrAdmin && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowReminders(!showReminders)}
                        className={cn("gap-1.5 text-xs", showReminders && "bg-[#FF914D]/10 text-[#FF914D]")}
                        data-testid="toggle-reminders-btn"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Rappels IA</span>
                    </Button>
                )}
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Messages */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {loading ? (
                            <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                        ) : messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center">
                                <p className="text-muted-foreground text-sm py-12">Début de la conversation</p>
                                {isSeller && templates.length > 0 && (
                                    <div className="max-w-sm">
                                        <p className="text-xs text-muted-foreground mb-3">Utilisez une suggestion pour démarrer :</p>
                                        <GuidedPanel templates={templates} onSelect={handleGuidedSelect} isDark={isDark} />
                                    </div>
                                )}
                            </div>
                        ) : messages.map(msg => {
                            const isMine = msg.sender_id === userId;
                            const isGuided = msg.message_type === 'guided';
                            return (
                                <div key={msg.id} className={cn("flex", isMine ? "justify-end" : "justify-start")} data-testid={`message-${msg.id}`}>
                                    <div className={cn(
                                        "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
                                        isMine
                                            ? "bg-gradient-to-r from-[#681793] to-[#9b4dca] text-white rounded-br-md"
                                            : isDark
                                                ? "bg-white/10 text-white rounded-bl-md"
                                                : "bg-slate-100 text-slate-900 rounded-bl-md"
                                    )}>
                                        {!isMine && isGroup && (
                                            <p className="text-[10px] font-semibold text-[#FF914D] mb-0.5">{msg.sender_name}</p>
                                        )}
                                        {!isMine && !isGroup && (
                                            <p className="text-[10px] font-semibold text-[#FF914D] mb-0.5">{msg.sender_name}</p>
                                        )}
                                        {isGuided && msg.guided_category && (
                                            <div className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold mb-1.5",
                                                isMine ? "bg-white/20 text-white" : isDark ? "bg-[#FF914D]/10 text-[#FF914D]" : "bg-[#FF914D]/10 text-[#FF914D]"
                                            )}>
                                                <Bell className="w-3 h-3" />
                                                {GUIDED_TEMPLATES.find(t => t.id === msg.guided_category)?.label || msg.guided_category}
                                            </div>
                                        )}
                                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                        <p className={cn("text-[10px] mt-1", isMine ? "text-white/50 text-right" : "text-muted-foreground")}>
                                            {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={bottomRef} />
                    </div>

                    {/* Guided suggestions toggle (seller) */}
                    {isSeller && templates.length > 0 && showGuided && (
                        <div className={cn("border-t", isDark ? "border-white/10" : "border-slate-200")}>
                            <GuidedPanel templates={templates} onSelect={handleGuidedSelect} isDark={isDark} />
                        </div>
                    )}

                    {/* Input */}
                    <div className={cn("p-3 border-t", isDark ? "border-white/10" : "border-slate-200")}>
                        {guidedCategory && (
                            <div className="flex items-center gap-2 mb-2 px-1">
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF914D]/10 text-[#FF914D] font-bold flex items-center gap-1">
                                    <Bell className="w-3 h-3" />
                                    {GUIDED_TEMPLATES.find(t => t.id === guidedCategory)?.label}
                                </span>
                                <button onClick={() => { setGuidedCategory(null); setNewMsg(''); }} className="text-[10px] text-muted-foreground hover:text-foreground">
                                    Annuler
                                </button>
                            </div>
                        )}
                        <div className="flex items-end gap-2">
                            {isSeller && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowGuided(!showGuided)}
                                    className={cn("shrink-0 h-10 w-10", showGuided && "bg-[#FF914D]/10 text-[#FF914D]")}
                                    data-testid="toggle-guided-btn"
                                >
                                    <Sparkles className="w-4 h-4" />
                                </Button>
                            )}
                            <textarea
                                ref={inputRef}
                                value={newMsg}
                                onChange={e => setNewMsg(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={isSeller ? "Écrivez un message ou utilisez une suggestion..." : "Écrivez un message..."}
                                rows={1}
                                className={cn(
                                    "flex-1 rounded-xl px-4 py-2.5 resize-none text-sm border focus:outline-none focus:ring-2 focus:ring-[#681793]/50 min-h-[40px] max-h-[120px]",
                                    isDark ? "bg-white/5 border-white/10 text-white placeholder:text-white/30" : "bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400"
                                )}
                                data-testid="message-input"
                            />
                            <Button onClick={handleSend} disabled={!newMsg.trim() || sending} className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white h-10 w-10 p-0 rounded-xl shrink-0" data-testid="send-message-btn">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* AI Reminders sidebar */}
                {showReminders && isAgentOrAdmin && (
                    <AiRemindersPanel conversationId={conversationId} isDark={isDark} onClose={() => setShowReminders(false)} />
                )}
            </div>
        </div>
    );
}

// ─── Tab constants ────────────────────────────────────
const GUIDED_TEMPLATES = [
    { id: 'callback', label: 'Demande de rappel' },
    { id: 'price', label: 'Question sur le prix' },
    { id: 'visit', label: 'Retour de visite' },
    { id: 'timeline', label: 'Délais et planning' },
    { id: 'docs', label: 'Documents' },
    { id: 'issue', label: 'Problème' },
];

// ─── Main Page ────────────────────────────────────────
export default function MessagingPage() {
    const { user, isSeller } = useAuth();
    const { isDark } = useTheme();
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [showNewPanel, setShowNewPanel] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(isSeller ? 'client' : 'agency');
    const userId = user?.id;
    const userRole = user?.role;
    const isAgentOrAdmin = userRole === 'admin' || userRole === 'agent';

    const loadConversations = useCallback(async () => {
        try {
            const res = await messagingAPI.getConversations(1, isAgentOrAdmin ? activeTab : null);
            setConversations(res.data.conversations || []);
        } catch { /* */ }
        setLoading(false);
    }, [activeTab, isAgentOrAdmin]);

    useEffect(() => {
        setLoading(true);
        setActiveConversation(null);
        loadConversations();
        const interval = setInterval(loadConversations, 8000);
        return () => clearInterval(interval);
    }, [loadConversations]);

    const handleSelectConversation = (id) => {
        setActiveConversation(id);
        setShowNewPanel(false);
        loadConversations();
    };

    const handleConversationCreated = (convId) => {
        setShowNewPanel(false);
        setActiveConversation(convId);
        loadConversations();
    };

    const handleCreateAgencyGroup = async () => {
        try {
            const res = await messagingAPI.createAgencyGroup();
            handleSelectConversation(res.data.conversation_id);
        } catch { /* */ }
    };

    const showChat = activeConversation && !showNewPanel;
    const showNew = showNewPanel;

    return (
        <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-2rem)] flex" data-testid="messaging-page">
            <div className={cn(
                "border rounded-2xl overflow-hidden flex w-full",
                isDark ? "bg-card/50 border-white/10 backdrop-blur-xl" : "bg-white border-slate-200 shadow-sm"
            )}>
                {/* Left panel */}
                <div className={cn(
                    "w-full md:w-80 lg:w-96 border-r flex flex-col shrink-0",
                    isDark ? "border-white/10" : "border-slate-200",
                    (showChat || showNew) ? "hidden md:flex" : "flex"
                )}>
                    {showNew ? (
                        <NewConversationPanel
                            onClose={() => setShowNewPanel(false)}
                            onCreated={handleConversationCreated}
                            isDark={isDark}
                            contactType={isAgentOrAdmin ? activeTab : null}
                        />
                    ) : (
                        <>
                            {/* Header with tabs */}
                            <div className={cn("border-b", isDark ? "border-white/10" : "border-slate-200")}>
                                <div className="p-3 pb-0 flex items-center justify-between">
                                    <h2 className="font-bold text-lg flex items-center gap-2">
                                        <MessageSquare className="w-5 h-5 text-[#FF914D]" />
                                        Messages
                                    </h2>
                                    <div className="flex items-center gap-1.5">
                                        {isAgentOrAdmin && activeTab === 'agency' && (
                                            <Button
                                                onClick={handleCreateAgencyGroup}
                                                size="sm"
                                                variant="ghost"
                                                className="text-xs h-7 px-2 text-muted-foreground"
                                                data-testid="create-agency-group-btn"
                                            >
                                                <Users className="w-3.5 h-3.5 mr-1" /> Groupe
                                            </Button>
                                        )}
                                        <Button
                                            onClick={() => setShowNewPanel(true)}
                                            size="sm"
                                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white h-7 px-2.5 text-xs"
                                            data-testid="new-conversation-btn"
                                        >
                                            <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau
                                        </Button>
                                    </div>
                                </div>

                                {/* Tabs for Agent/Admin */}
                                {isAgentOrAdmin && (
                                    <div className="flex px-3 pt-3 gap-1" data-testid="messaging-tabs">
                                        <button
                                            onClick={() => setActiveTab('agency')}
                                            className={cn(
                                                "flex-1 flex items-center justify-center gap-1.5 pb-2.5 text-xs font-semibold border-b-2 transition-colors",
                                                activeTab === 'agency'
                                                    ? "border-[#681793] text-[#681793]"
                                                    : "border-transparent text-muted-foreground hover:text-foreground"
                                            )}
                                            data-testid="tab-agency"
                                        >
                                            <Building2 className="w-3.5 h-3.5" /> Agence
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('client')}
                                            className={cn(
                                                "flex-1 flex items-center justify-center gap-1.5 pb-2.5 text-xs font-semibold border-b-2 transition-colors",
                                                activeTab === 'client'
                                                    ? "border-[#FF914D] text-[#FF914D]"
                                                    : "border-transparent text-muted-foreground hover:text-foreground"
                                            )}
                                            data-testid="tab-client"
                                        >
                                            <User className="w-3.5 h-3.5" /> Clients
                                        </button>
                                    </div>
                                )}
                            </div>

                            <ConversationList
                                conversations={conversations}
                                activeId={activeConversation}
                                onSelect={handleSelectConversation}
                                onNewClick={() => setShowNewPanel(true)}
                                isDark={isDark}
                                userId={userId}
                            />
                        </>
                    )}
                </div>

                {/* Right panel — chat */}
                <div className={cn("flex-1 flex flex-col min-w-0", !showChat ? "hidden md:flex" : "flex")}>
                    {activeConversation ? (
                        <ChatView
                            conversationId={activeConversation}
                            onBack={() => setActiveConversation(null)}
                            isDark={isDark}
                            userId={userId}
                            userRole={userRole}
                        />
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 flex items-center justify-center mb-5">
                                <MessageSquare className="w-10 h-10 text-[#FF914D]" />
                            </div>
                            <h3 className="font-bold text-lg mb-2">Messagerie Homly</h3>
                            <p className="text-sm text-muted-foreground max-w-xs">
                                {isAgentOrAdmin
                                    ? "Échangez avec votre équipe ou vos clients. L'IA peut extraire des rappels intelligents de vos conversations."
                                    : "Échangez avec votre agent immobilier. Utilisez les suggestions rapides pour faciliter la communication."
                                }
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
